import { useQuery } from "@tanstack/react-query";
import {
  getActivityFeed,
  type ActivityEvent,
  type ActivityFeedResponse,
} from "../../lib/api-modules/portfolio.js";
import { Card, Pill } from "../design-system/index.js";
import { formatCentsCompact } from "../../lib/util/money.js";

/**
 * Phase 11 — Activity feed dashboard tile. Renders the household's
 * most-recent milestone events from `activity_events` (populated by
 * the ActivityFeedFn DDB-stream consumer on net_worth_snapshots).
 */
export function ActivityFeedTile() {
  const { data, isLoading, error } = useQuery<ActivityFeedResponse>({
    queryKey: ["portfolio", "activity-feed", 10],
    queryFn: () => getActivityFeed(10),
  });

  return (
    <Card>
      <h3 className="mb-3 font-display text-lg font-medium text-[var(--color-text)]">
        Recent milestones
      </h3>
      {isLoading && <p className="text-sm text-[var(--color-muted)]">Loading…</p>}
      {error && (
        <p className="text-sm text-[var(--color-danger)]">
          {error instanceof Error ? error.message : "Failed"}
        </p>
      )}
      {data && data.events.length === 0 && (
        <p className="text-sm text-[var(--color-muted)]">
          No milestones yet. As your net worth or FIRE progress crosses
          round-number thresholds, they'll appear here.
        </p>
      )}
      {data && data.events.length > 0 && (
        <ul className="space-y-2">
          {data.events.map((e) => (
            <ActivityRow key={e.event_id} event={e} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ActivityRow({ event }: { event: ActivityEvent }) {
  const date = event.occurred_at
    ? event.occurred_at.slice(0, 10)
    : null;
  let headline = event.event_id;
  if (event.event_type === "net_worth_milestone") {
    const t = (event.event_data["threshold_cents"] as number | undefined) ?? 0;
    headline = `Crossed ${formatCentsCompact(t)} net worth`;
  } else if (event.event_type === "fire_progress_milestone") {
    const pct = event.event_data["pct"] as number | undefined;
    headline = `Reached ${pct}% of FIRE target`;
  }
  return (
    <li className="flex items-start gap-3">
      <Pill
        tone={
          event.event_type === "fire_progress_milestone" ? "accent" : "success"
        }
      >
        {event.event_type.replace(/_/g, " ")}
      </Pill>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-[var(--color-text)]">{headline}</p>
        {date && (
          <p className="text-xs text-[var(--color-muted)]">{date}</p>
        )}
      </div>
    </li>
  );
}
